import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { GridController } from '../../classes/grid-page-info';
import {
  DropDownItem,
  FilterItems,
  type ISelect,
} from '../../components/filters/filter.interface';
import { IUser, UserFilters } from '../../interfaces/user/user.interface';
import { CommonGraphqlService } from '../common.graphql.service';
import { TSortDirections } from './../../classes/grid-page-info';
import { UserStateService } from './user-state.service';

@Injectable({
  providedIn: 'root',
})
export class UsersStateService extends GridController<UserFilters> {
  private defaultUserFilter: UserFilters = {
    userName: '',
    firstName: '',
    lastName: '',
    groupId: '',
    vendor: '',
    ext: '',
    includeInactive: ['D'],
    accessLevel: undefined,
    active: 'Any',
    passwordResetAllowed: 'Any',
  };

  userFilters: FilterItems = {
    filtersName: 'Users',
    items: [
      {
        caption: 'Login',
        name: 'userName',
        type: 'text',
        placeholder: 'Enter Login',
        class: 'userName',
      },
      {
        caption: 'First Name',
        name: 'firstName',
        type: 'text',
        placeholder: 'Enter First Name',
        class: 'firstName',
      },
      {
        caption: 'Last Name',
        name: 'lastName',
        type: 'text',
        placeholder: 'Enter Last Name',
        class: 'lastName',
      },
      {
        caption: 'Group Id',
        name: 'groupId',
        type: 'text',
        placeholder: 'Enter Group ID',
        class: 'groupId',
      },
      {
        caption: 'Vendor',
        name: 'vendor',
        type: 'select',
        placeholder: 'Select Vendor',
        asyncValues: undefined,
        class: 'vendor',
      },
      {
        caption: 'Status',
        name: 'active',
        type: 'select',
        placeholder: 'Select Status',
        values: [
          { key: 1, value: 'Active' },
          { key: 2, value: 'Inactive' },
        ],
        class: 'status',
      },
    ],
  };

  getApplications(): Observable<DropDownItem[]> {
    const vendors = this.userStateService.user?.applications ?? [];
    const vendorCodes =
      vendors.length > 0
        ? vendors.map((x) => {
            return x.code;
          })
        : undefined;

    return this.commonGraphqlService
      .getApplications({ codes: vendorCodes })
      .pipe(
        map((apps) => {
          return apps.map((a) => {
            return { key: a.code, value: a.description };
          });
        }),
      );
  }

  override _sortDirections: TSortDirections = {
    firstName: '',
    userName: 'ASC',
    accessLevel: '',
    role: '',
  };

  private _users: IUserDTO[];

  constructor(
    private commonGraphqlService: CommonGraphqlService,
    private userStateService: UserStateService,
  ) {
    super();
    this.init(this.defaultUserFilter, 1, 'userName');

    const vendorDropDown = this.userFilters.items?.find((x) => {
      return x.name === 'vendor';
    }) as ISelect;
    vendorDropDown.asyncValues = this.getApplications();

    if (this.userStateService.user?.accessLevel === 'SECURE_CONNECT') {
      this.userFilters.items?.push({
        caption: 'User Type',
        name: 'accessLevel',
        type: 'select',
        placeholder: 'Select Status',
        values: [
          {
            key: 'SECURE_CONNECT',
            value: 'SECURE_CONNECT',
          },
          {
            key: 'VENDOR',
            value: 'VENDOR',
          },
          {
            key: 'ACCOUNT',
            value: 'ACCOUNT',
          },
          {
            key: 'BILLING_GROUP',
            value: 'BILLING_GROUP',
          },
        ],
      });
    }
  }

  get users(): IUserDTO[] {
    return this._users;
  }

  set users(value: IUserDTO[]) {
    this._users = value;
  }
}

export type IUserDTO = {
  isExpanded: boolean;
  isEditedFromGrid: boolean;
  isLoading: boolean;
} & IUser;
